import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import I18n from 'i18n.js.erb'
import EInkSmartLabel from "./EInkSmartLabel";
import { CURRENCY_EURO } from "../../utils/utils";

// L'etichetta puo' seguire la lingua della carta, l'inglese, oppure quello che ci scrive il negoziante
const ORIGINAL = 'original';
const ENGLISH = 'english';
const LINE_KEYS = ['line_1', 'line_2', 'line_3', 'line_4', 'line_5'];

const sameLines = (a, b) => LINE_KEYS.every(key => (a[key] || '') === (b[key] || ''));

// Con quale delle due versioni generate coincide l'etichetta di adesso: se non coincide con nessuna
// e' stata scritta a mano, e allora nessuna delle due resta selezionata
const matchingLanguage = (generated, label) => {
  if (sameLines(generated.original, label)) return ORIGINAL;
  if (sameLines(generated.english, label)) return ENGLISH;
  return null;
};

const LoopCellEditModal = ({
  showModal,
  cell,
  handleCloseModal,
  onUpdateSuccess,
}) => {
  const [price, setPrice] = useState('');
  const [lines, setLines] = useState({ line_1: '', line_2: '', line_3: '', line_4: '', line_5: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [activeTab, setActiveTab] = useState(ORIGINAL);
  const [generatedLines, setGeneratedLines] = useState(null);

  useEffect(() => {
    if (showModal && cell) {
      const label = cell.label || {};
      const formattedPrice = label.price
        ? parseFloat(label.price).toFixed(2).replace('.', ',')
        : '';
      setPrice(formattedPrice);
      setLines({
        line_1: label.line_1 || '',
        line_2: label.line_2 || '',
        line_3: label.line_3 || '',
        line_4: label.line_4 || '',
        line_5: label.line_5 || '',
      });
      setError(false);
      setActiveTab(ORIGINAL);
      setGeneratedLines(null);
    }
  }, [showModal, cell]);

  // Le due versioni generate arrivano dal server all'apertura, cosi' il passaggio da una lingua
  // all'altra e' immediato. Se la chiamata fallisce si resta con la sola modifica a mano
  useEffect(() => {
    if (!showModal || !cell?.serial_number) return;

    let cancelled = false;

    fetch(`/grading_certificate/${cell.serial_number}/label_languages`, { method: "GET" })
      .then((response) => response.json())
      .then((data) => {
        if (cancelled || !data?.original) return;
        setGeneratedLines(data);
        setActiveTab(matchingLanguage(data, cell.label || {}) || ORIGINAL);
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [showModal, cell]);

  const handlePriceChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*[,.]?\d{0,2}$/.test(value)) {
      setPrice(value);
    }
  };

  const parsePrice = (priceString) => {
    if (!priceString) return 0;
    return parseFloat(priceString.replace(',', '.'));
  };

  const handleLineChange = (key, value) => {
    setLines(prev => ({ ...prev, [key]: value }));
  };

  // La tab non e' uno stato dell'etichetta, e' un comando: riporta le righe ai valori di quella lingua.
  // Quello che poi il negoziante ci scrive sopra resta suo, e la tab non lo rinnega
  const handleLanguageChange = (value) => {
    if (!generatedLines) return;

    setActiveTab(value);
    const source = value === ENGLISH ? generatedLines.english : generatedLines.original;
    setLines({
      line_1: source.line_1 || '',
      line_2: source.line_2 || '',
      line_3: source.line_3 || '',
      line_4: source.line_4 || '',
      line_5: source.line_5 || '',
    });
  };

  const csrfToken = () => document.querySelector('[name="csrf-token"]')?.content;

  const handleSubmit = async () => {
    if (!cell) return;

    setIsLoading(true);
    setError(false);

    const label = cell.label || {};
    const newPrice = parsePrice(price);
    const priceChanged = newPrice > 0 && newPrice !== parseFloat(label.price);
    const linesChanged = LINE_KEYS.some(key => lines[key] !== (label[key] || ''));

    if (!priceChanged && !linesChanged) {
      setIsLoading(false);
      handleCloseModal();
      return;
    }

    const body = {};
    // Se le righe coincidono con quelle generate nella lingua della carta si toglie l'override invece
    // di congelarne il testo, cosi' l'etichetta continua a seguire i dati della carta. Si guarda il
    // testo e non la tab aperta: la tab e' solo il comando che lo ha riempito
    const isBackToDefault = generatedLines && sameLines(generatedLines.original, lines);
    if (linesChanged && isBackToDefault) body.reset_loop_label_content = true;
    else if (linesChanged) body.loop_label_content = lines;
    if (priceChanged) body.price = newPrice;

    try {
      const res = await fetch(`/grading_certificate/${cell.serial_number}`, {
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('update_failed');
      onUpdateSuccess(cell, priceChanged ? newPrice : null, linesChanged ? lines : null);
      handleCloseModal();
    } catch {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (!cell) return null;

  return (
    <Modal
      id="loop-cell-edit-modal"
      show={showModal}
      onHide={handleCloseModal}
      animation={false}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title className="w-100 text-center mt-1">
          {I18n.t('js.loopCellModal.title')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="text-center mb-3">
          <p className="font-size-14px text-muted mb-2">
            {I18n.t('js.loopCellModal.certificateNumber')}: <b>{cell.serial_number}</b>
          </p>
        </div>

        <div className="mb-3">
          <label className="font-size-13px mb-2 d-block">
            <b>{I18n.t('js.loopCellModal.priceLabel')}</b>
          </label>
          <div className="d-flex flex-nowrap align-items-center input-group">
            <input
              type="text"
              inputMode="decimal"
              value={price}
              className="form-control"
              placeholder={I18n.t('js.loopCellModal.pricePlaceholder')}
              onChange={handlePriceChange}
            />
            <div className="input-group-append">
              <span className="input-group-text bg-intense-blue-imp text-white border-none">
                {CURRENCY_EURO}
              </span>
            </div>
          </div>
        </div>

        <hr className="my-3" />

        <div className="mb-2">
          <p className="font-size-13px mb-1">
            <b>{I18n.t('js.loopCellModal.labelLinesTitle')}</b>
          </p>
          <p className="font-size-12px text-muted mb-3">
            {I18n.t('js.loopCellModal.labelLinesDescription')}
          </p>
        </div>

        {/* Le due lingue non descrivono lo stato dell'etichetta: premendole si riempiono le righe con
            i valori di quella lingua, che restano modificabili */}
        <ul className="loop-label-tabs">
          {[
            { value: ORIGINAL, text: I18n.t('js.loopCellModal.labelLanguageOriginal') },
            { value: ENGLISH, text: I18n.t('js.loopCellModal.labelLanguageEnglish') },
          ].map(({ value, text }) => (
            <li key={value}>
              <button
                type="button"
                className={activeTab === value ? 'active' : ''}
                disabled={!generatedLines}
                onClick={() => handleLanguageChange(value)}
              >
                {text}
              </button>
            </li>
          ))}
        </ul>

        {/* Gemello dell'etichetta vera: si modifica riga per riga dove la riga si trova davvero */}
        <div className="loop-label-twin mb-3">
          <EInkSmartLabel
            cell={{ ...cell, label: { ...lines, price: parsePrice(price) } }}
            onLineChange={handleLineChange}
          />
        </div>

        {error && (
          <div className="alert alert-danger font-size-12px mt-3 mb-0">
            <i className="fas fa-exclamation-circle mr-2"></i>
            {I18n.t('js.loopCellModal.errorMessage')}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="btn-standard-outline br-20px pr-2 pl-2 w-120px btn-standard-outline-resized text-weight-500"
          onClick={handleCloseModal}
          disabled={isLoading}
        >
          {I18n.t('js.common.button.cancel').toUpperCase()}
        </button>

        <button
          type="button"
          className="btn-standard bg-aig-gradient text-light br-20px px-3 btn-standard-resized pt-6px-imp h-35px text-weight-500"
          disabled={isLoading}
          onClick={handleSubmit}
        >
          {isLoading ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          ) : (
            I18n.t('js.common.button.confirm').toUpperCase()
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default LoopCellEditModal;
